import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import { cn } from '../../../../../utils';
import type { DataGridColumnDef, DataGridColumnDefOptions, DurationFormatParts, DurationFormatStyle } from '../../data-grid.type';

export type DurationCellValue =
	/** Raw duration parts formatted inside the cell using Intl.DurationFormat */
	{
		duration: DurationFormatParts | null | undefined;
		style?: DurationFormatStyle;
		/**
		 * Optional className for the duration cell root.
		 */
		className?: string;
		fallback?: React.ReactNode;
	};

export interface DurationCellProps {
	value: string;
	unit: string;
	className?: string;
}

const DurationCell: React.FC<DurationCellProps> = ({ value, unit, className }) => {
	return (
		<div
			data-aid="data-grid-duration-cell"
			data-testid="data-grid-duration-cell"
			className={cn(
				'flex flex-col justify-center text-left',
				className
			)}
		>
			<div
				data-testid="data-grid-duration-cell-value"
				className="text-sm text-primary leading-tight"
			>
				{value}
			</div>
			<div
				data-testid="data-grid-duration-cell-unit"
				className="text-xs text-secondary leading-tight capitalize"
			>
				{unit}
			</div>
		</div>
	);
};

type DurationUnitKey = 'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds';
type IntlUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

const durationUnitOrder: DurationUnitKey[] = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'];

const durationUnitToIntlUnit: Record<DurationUnitKey, IntlUnit> = {
	years: 'year',
	months: 'month',
	weeks: 'week',
	days: 'day',
	hours: 'hour',
	minutes: 'minute',
	seconds: 'second',
};

const styleToUnitDisplay: Record<DurationFormatStyle, 'short' | 'long' | 'narrow'> = {
	short: 'short',
	long: 'long',
	narrow: 'narrow',
};

const getPrimaryDurationPart = (duration: DurationFormatParts): { unitKey: DurationUnitKey; value: number } | null => {
	for (const unitKey of durationUnitOrder) {
		const raw = duration[unitKey];
		if (typeof raw !== 'number' || Number.isNaN(raw) || raw === 0) continue;
		return { unitKey, value: raw };
	}
	return null;
};

const formatUnitToTwoLines = (
	locale: string | undefined,
	unit: IntlUnit,
	unitDisplay: 'short' | 'long' | 'narrow',
	value: number
): { valueText: string; unitText: string } => {
	try {
		const parts = new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay }).formatToParts(value);
		const valueText = parts
			.filter(p => p.type === 'integer' || p.type === 'decimal' || p.type === 'fraction' || p.type === 'group' || p.type === 'plusSign' || p.type === 'minusSign')
			.map(p => p.value)
			.join('')
			.trim();

		const unitText = parts
			.filter(p => p.type === 'unit')
			.map(p => p.value)
			.join('')
			.trim();

		return { valueText, unitText };
	} catch {
		// Very old runtimes might not support unit formatting.
		return { valueText: String(value), unitText: unit };
	}
};

export function getColumnDefDuration<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
	return function DurationCellColumn(context: CellContext<T, unknown>) {
		const value = context.getValue();
		const row = context.row.original;

		let durationValue: DurationCellValue | null = null;

		if (options?.parse) {
			const parsed = options.parse(value, row, options) as unknown;
			if (typeof parsed === 'object' && parsed !== null && 'duration' in parsed) {
				durationValue = parsed as DurationCellValue;
			}
		} else if (typeof value === 'object' && value !== null && 'duration' in value) {
			durationValue = value as DurationCellValue;
		}

		if (!durationValue) return '';

		const locale = options?.locale;
		const style = durationValue.style ?? 'short';
		const unitDisplay = styleToUnitDisplay[style];

		const primary = durationValue.duration ? getPrimaryDurationPart(durationValue.duration) : null;
		if (!primary) return durationValue.fallback ?? '';

		const formatted = formatUnitToTwoLines(locale, durationUnitToIntlUnit[primary.unitKey], unitDisplay, primary.value);

		return <DurationCell value={formatted.valueText} unit={formatted.unitText} className={durationValue.className} />;
	};
}
